import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { WaitlistForm } from "@/components/WaitlistForm";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Check, ArrowRight, ArrowLeft, Camera } from "lucide-react";
import { toast } from "sonner";

const INTERESTS = ["Live music", "Football", "Galleries", "Travel", "Coffee", "Film", "Food", "Nightlife", "Art", "Fashion", "Running", "Theatre"];
const GENDERS = ["Woman", "Man", "Non-binary"];
const PREFS = ["Women", "Men", "Everyone"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const fileRef = useRef();
  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    photos: [], name: "", age: "", bio: "", interests: [], city: "", country: "",
    gender: "", pref_gender: "Everyone", pref_age_min: 21, pref_age_max: 40, agreed: false,
  });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photos: [...f.photos, data.url] }));
    } catch {
      toast.error("Upload failed, try a smaller image");
    } finally {
      setUploading(false);
    }
  };

  const toggleInterest = (i) =>
    setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));

  const { data: areas = [] } = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/locations")).data });
  useEffect(() => {
    const def = areas.find((a) => a.is_default) || areas[0];
    if (def && !form.location_id) setForm((f) => ({ ...f, location_id: def.id, city: def.city }));
  }, [areas, form.location_id]);

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        name: form.name, age: Number(form.age), bio: form.bio, interests: form.interests,
        location_id: form.location_id, gender: form.gender,
        pref_gender: form.pref_gender, pref_age_min: Number(form.pref_age_min), pref_age_max: Number(form.pref_age_max),
        photos: form.photos,
      });
      await refresh();
      toast.success("Profile ready!");
      navigate("/discover", { replace: true });
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const canNext =
    step === 0 ? form.photos.length > 0 && form.name && form.age :
    step === 1 ? form.location_id && form.interests.length > 0 :
    form.gender && form.agreed;

  return (
    <div className="min-h-screen">
      <header className="px-5 h-20 flex items-center justify-between max-w-2xl mx-auto">
        <Logo size="text-2xl" />
        <span className="text-xs uppercase tracking-widest text-amber-400/70">Step {step + 1} of 3</span>
      </header>

      <div className="max-w-2xl mx-auto px-5 pb-28">
        <div className="flex gap-2 mb-8">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-amber-400" : "bg-white/10"}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="fade-up">
            <h1 className="font-display font-black text-3xl">Show your best self</h1>
            <p className="text-sm text-emerald-100/60 mt-2">Full-screen photos make the first impression. Add at least one.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
              {form.photos.map((p, i) => (
                <div key={i} className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-amber-500/20 pop">
                  <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
                  <button data-testid={`remove-photo-${i}`} onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, x) => x !== i) }))}
                    className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 flex items-center justify-center">
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              ))}
              {form.photos.length < 6 && (
                <button data-testid="add-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="aspect-[3/4] rounded-2xl border-2 border-dashed border-amber-500/30 flex flex-col items-center justify-center gap-2 text-amber-400/80 hover:border-amber-500/60 hover:bg-amber-500/5 transition-all">
                  {uploading ? <div className="h-6 w-6 rounded-full border-2 border-amber-400/30 border-t-amber-400 animate-spin" />
                    : <><Camera className="h-6 w-6" /><span className="text-xs font-semibold">Add photo</span></>}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="photo-input" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div>
                <Label className="text-emerald-100/70">Name</Label>
                <Input data-testid="onboard-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 h-11 rounded-xl" placeholder="Your name" />
              </div>
              <div>
                <Label className="text-emerald-100/70">Age</Label>
                <Input data-testid="onboard-age" type="number" min={18} max={99} value={form.age}
                  onChange={(e) => setForm({ ...form, age: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 h-11 rounded-xl" placeholder="24" />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="fade-up">
            <h1 className="font-display font-black text-3xl">Where & what you love</h1>
            <p className="text-sm text-emerald-100/60 mt-2">We'll match you to events around these.</p>
            <div className="mt-6">
              <Label className="text-emerald-100/70">Your area</Label>
              <Select value={form.location_id || ""} onValueChange={(v) => setForm({ ...form, location_id: v, city: areas.find((a) => a.id === v)?.city || "" })}>
                <SelectTrigger data-testid="onboard-city" className="mt-1.5 bg-white/5 border-white/10 h-11 rounded-xl">
                  <SelectValue placeholder="Choose your area" />
                </SelectTrigger>
                <SelectContent>{areas.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`onboard-area-${a.id}`}>{a.name}, {a.city}</SelectItem>)}</SelectContent>
              </Select>
              <p data-testid="onboard-area-note" className="text-xs text-emerald-100/50 mt-1.5">Alpha launch: you'll see people and events within {areas.find((a) => a.id === form.location_id)?.radius_miles || 15} miles of {areas.find((a) => a.id === form.location_id)?.name || "Clapham"}.</p>
              <div className="mt-3"><WaitlistForm source="onboarding" defaultEmail={user?.email || ""} compact /></div>
            </div>
            <div className="mt-6">
              <Label className="text-emerald-100/70 mb-3 block">Interests</Label>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((i) => {
                  const on = form.interests.includes(i);
                  return (
                    <button key={i} data-testid={`interest-${i}`} onClick={() => toggleInterest(i)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                        on ? "bg-pink-500/20 border-pink-400/50 text-pink-200" : "border-white/10 text-emerald-100/70 hover:border-white/25"
                      }`}>{i}</button>
                  );
                })}
              </div>
            </div>
            <div className="mt-6">
              <Label className="text-emerald-100/70">About you</Label>
              <Textarea data-testid="onboard-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10 rounded-xl min-h-24" placeholder="A line about your perfect night out…" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="fade-up">
            <h1 className="font-display font-black text-3xl">Your preferences</h1>
            <div className="mt-6">
              <Label className="text-emerald-100/70 mb-3 block">I am a</Label>
              <div className="flex flex-wrap gap-2">
                {GENDERS.map((g) => (
                  <button key={g} data-testid={`gender-${g}`} onClick={() => setForm({ ...form, gender: g })}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                      form.gender === g ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-200" : "border-white/10 text-emerald-100/70"
                    }`}>{g}</button>
                ))}
              </div>
            </div>
            <div className="mt-6">
              <Label className="text-emerald-100/70 mb-3 block">Show me</Label>
              <div className="flex flex-wrap gap-2">
                {PREFS.map((p) => (
                  <button key={p} data-testid={`pref-${p}`} onClick={() => setForm({ ...form, pref_gender: p })}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                      form.pref_gender === p ? "bg-amber-500/20 border-amber-400/50 text-amber-200" : "border-white/10 text-emerald-100/70"
                    }`}>{p}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div>
                <Label className="text-emerald-100/70">Min age</Label>
                <Input data-testid="pref-age-min" type="number" value={form.pref_age_min}
                  onChange={(e) => setForm({ ...form, pref_age_min: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-emerald-100/70">Max age</Label>
                <Input data-testid="pref-age-max" type="number" value={form.pref_age_max}
                  onChange={(e) => setForm({ ...form, pref_age_max: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 h-11 rounded-xl" />
              </div>
            </div>
            <button data-testid="agree-guidelines" onClick={() => setForm({ ...form, agreed: !form.agreed })}
              className="mt-6 w-full glass rounded-2xl p-4 flex items-center gap-3 text-left">
              <span className={`h-6 w-6 rounded-md flex items-center justify-center border ${form.agreed ? "bg-emerald-500 border-emerald-500" : "border-white/20"}`}>
                {form.agreed && <Check className="h-4 w-4 text-black" />}
              </span>
              <span className="text-sm text-emerald-100/70">I agree to MoYo Stars Date's community & safety guidelines — be respectful, be real, and keep every vibe check consensual.</span>
            </button>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 inset-x-0 glass border-t border-amber-500/10">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          {step > 0 ? (
            <Button data-testid="onboard-back" variant="ghost" onClick={() => setStep(step - 1)} className="rounded-full text-emerald-100/70">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          ) : <span />}
          {step < 2 ? (
            <Button data-testid="onboard-next" disabled={!canNext} onClick={() => setStep(step + 1)} className="btn-gold rounded-full h-11 px-8">
              Continue <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button data-testid="onboard-finish" disabled={!canNext || saving} onClick={finish} className="btn-gold rounded-full h-11 px-8">
              {saving ? "Saving…" : "Enter MoYo Stars Date"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
